// hooks/useCalendarHandlers.ts
import { useCallback } from "react";
import { MbscEventCreatedEvent, MbscEventUpdatedEvent, MbscCalendarEvent, Eventcalendar } from "@mobiscroll/react";
import { Reservation, ReservationStatus } from "crqlar-react-common/dist/models/reservation/reservation.model";
import { ReservationService } from "crqlar-react-common/dist/services/reservation/reservation.service";
import { addMinutes, diffInMinutes } from "crqlar-react-common/dist/utils/date.util";
import { toJsDate } from "crqlar-react-common/dist/utils/calendarTimezone";
import { Resource } from "crqlar-react-common/dist/models/resource/resource.model";
import { Service } from "crqlar-react-common/dist/models/service/service.model";
import { Sector } from "crqlar-react-common/dist/models/sector/sector.model";
import { useDispatch } from "react-redux";
import {ReservationActions} from "../../../../store/reservations.reducer";

interface UseCalendarHandlersParams {
    appId: string;
    sector?: Sector;
    defaultServiceId?: string;
    services: Service[];
    resourcesFilteredByCategory: Resource[];
    childrenResourcesFilteredByCategory: Resource[];
    mbscResourceColumnType: "services" | "resources" | undefined;
    calendarRef: React.MutableRefObject<Eventcalendar | null>;

    // Wizard state
    tmpReservationGroupWizardActive: boolean;
    tmpReservationGroupWizardReservation?: Reservation;
    tmpReservationGroupReservations: Reservation[];

    // State setters
    setMarkedReservation: (r: Reservation | undefined) => void;
    selectedEventFromSearch: (MbscCalendarEvent | null | undefined);
    setSelectedEventFromSearch: (e: MbscCalendarEvent | null) => void;
    setTmpUpdatedReservation: (r: Reservation | undefined) => void;
    setTmpOriginalReservation: (r: Reservation | undefined) => void;

    // Queries
    refetchData: () => Promise<void>;
}

export function useCalendarHandlers({
                                        appId,
                                        sector,
                                        defaultServiceId,
                                        services,
                                        resourcesFilteredByCategory,
                                        childrenResourcesFilteredByCategory,
                                        mbscResourceColumnType,
                                        calendarRef,
                                        tmpReservationGroupWizardActive,
                                        tmpReservationGroupWizardReservation,
                                        tmpReservationGroupReservations,
                                        setMarkedReservation,
                                        selectedEventFromSearch,
                                        setSelectedEventFromSearch,
                                        setTmpUpdatedReservation,
                                        setTmpOriginalReservation,
                                        refetchData,
                                    }: UseCalendarHandlersParams) {
    const dispatch = useDispatch();

    const handleDeselect = useCallback(() => {
        setSelectedEventFromSearch(null);
    }, [setSelectedEventFromSearch]);

    const createNewReservation = (event: MbscEventCreatedEvent) => {
        const resource =
            childrenResourcesFilteredByCategory.find(r => r.id === event.event.resource) ||
            resourcesFilteredByCategory.find(r => r.id === event.event.resource);

        const service = services.find(s => s.id === event.event.resource);

        const tmpReservation: Reservation = {
            expectedStartDate: toJsDate(event.event.start),
            expectedEndDate: toJsDate(event.event.end),
            status: ReservationStatus.CONFIRMED,
            resourceIds: [resource?.parentId, resource?.id].filter(Boolean) as string[],
            sectorId: sector?.id,
            serviceId: service?.id ?? defaultServiceId,
        };

        if (tmpReservationGroupWizardActive) {
            const updatedWizardReservation = {
                ...tmpReservationGroupWizardReservation,
                expectedStartDate: toJsDate(event.event.start),
                expectedEndDate: toJsDate(event.event.end),
                resourceIds: [resource?.parentId, resource?.id].filter(Boolean) as string[],
            };

            const oldIndex = tmpReservationGroupReservations.indexOf(tmpReservationGroupWizardReservation!);
            const updatedTmpReservations = [...tmpReservationGroupReservations];
            updatedTmpReservations[oldIndex] = updatedWizardReservation;

            dispatch({
                type: ReservationActions.SET_RESERVATIONS,
                appId,
                payload: {
                    tmpReservationGroupDialogOpen: true,
                    tmpReservationGroupWizardActive: false,
                    tmpReservationGroupReservations: updatedTmpReservations,
                },
            });
            return;
        }

        setMarkedReservation(tmpReservation);
    }

    const updateReservationViaCalendarAction = useCallback(
        async (calendarActionArgs: MbscEventUpdatedEvent) => {
            const event = calendarActionArgs.event;
            if (!event) {
                return { originalReservation: undefined, updatedReservation: undefined };
            }

            const oldEvent = calendarActionArgs.oldEvent;
            const reservationId = oldEvent?.reservationId as string;
            const originalReservation = await ReservationService.getReservationById(reservationId);
            const oldPrimaryResourceId = oldEvent?.parentResourceId as string;
            const oldMbscResourceId = oldEvent?.resource as string;
            const newMbscResourceId = event.resource as string;

            const updatedReservation: Reservation = { ...originalReservation };

            if (mbscResourceColumnType === 'services') {
                updatedReservation.serviceId = newMbscResourceId;
            } else {
                if (oldMbscResourceId === oldPrimaryResourceId) {
                    // Primary resources only (e.g., Spa)
                    updatedReservation.resourceIds =
                        updatedReservation.resourceIds?.filter(r => r !== oldPrimaryResourceId) || [];
                    if (newMbscResourceId) {
                        updatedReservation.resourceIds.push(newMbscResourceId);
                    }
                } else {
                    // Child resources (e.g., restaurant tables)
                    const newPrimaryResourceId =
                        childrenResourcesFilteredByCategory.find(r => r.id === newMbscResourceId)?.parentId;

                    if (oldPrimaryResourceId !== newPrimaryResourceId) {
                        updatedReservation.resourceIds = [newMbscResourceId];
                        if (newPrimaryResourceId) {
                            updatedReservation.resourceIds.push(newPrimaryResourceId);
                        }
                    } else {
                        updatedReservation.resourceIds =
                            updatedReservation.resourceIds?.filter(r => r !== oldMbscResourceId) || [];
                        if (newMbscResourceId) {
                            updatedReservation.resourceIds.push(newMbscResourceId);
                        }
                    }
                }
            }

            const startTimeDiff = diffInMinutes(toJsDate(event.start), toJsDate(oldEvent?.start));
            const endTimeDiff = diffInMinutes(toJsDate(event.end), toJsDate(oldEvent?.end));

            updatedReservation.expectedStartDate = addMinutes(updatedReservation.expectedStartDate!, startTimeDiff);
            updatedReservation.expectedEndDate = addMinutes(updatedReservation.expectedEndDate!, endTimeDiff);

            return { originalReservation, updatedReservation };
        },
        [mbscResourceColumnType, childrenResourcesFilteredByCategory]
    );

    const handleClickSearchResult = useCallback(
        (args: { event: MbscCalendarEvent }) => {
            const clickedEvent = args.event;

            if (selectedEventFromSearch?.id !== clickedEvent.id) {
                setSelectedEventFromSearch(clickedEvent);
                calendarRef.current?.navigateToEvent(clickedEvent);
            } else {
                setSelectedEventFromSearch(null);
                handleDeselect();
            }
        },
        [calendarRef, handleDeselect, selectedEventFromSearch, setSelectedEventFromSearch]
    );

    const handleEventQuickUpdate = useCallback(
        async (reservation: Reservation | undefined, notify: boolean) => {
            if (!reservation) return;

            const updatedReservationResponse = await ReservationService.updateReservation(reservation, notify);
            await refetchData();

            return updatedReservationResponse;
        },
        [refetchData]
    );

    const cancelWizard = useCallback(() => {
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                tmpReservationGroupDialogOpen: true,
                tmpReservationGroupWizardActive: false,
            },
        });
    }, [appId, dispatch]);

    return {
        handleDeselect,
        createNewReservation,
        updateReservationViaCalendarAction,
        handleClickSearchResult,
        handleEventQuickUpdate,
        cancelWizard,
    }
}
